import { useState, useContext, useEffect } from "react";
import BorderChangeNotice from "../BorderChangeNotice";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { successToast, errorToast } from "../../../toastAlerts/ToastAlerts";
import { AdminPrimaryContext } from "../../../adminContext/AdminContext";
import SentItem from "./SentItem";
import {
  CheckBox,
  SelectInputValueId,
} from "../../../inputComponents/inputcomp";
import axiosAdmin from "../../../../../api/apiAdmin";
import RecipientAddressModal from "../RecipientAddressModal";
import PaginatedItems from "../../../Pagination/Pagination";

//icons
import { FaRegHandPointRight } from "react-icons//fa";
import { BiLoaderAlt } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import { apiError } from "../../../../../api/apiError";
import AdminDownloadLink from "../../../AdminDownloadLink";

const SentPackage = () => {
  const { t } = useTranslation();
  //current filial
  const [currentPlaceId, setCurrentPlaceId] = useState(0);
  // Customs flags the last edit changed on OTHER parcels in the group.
  const [borderNotice, setBorderNotice] = useState(null);
  //branches data
  const { foreignAdresses } = useContext(AdminPrimaryContext);
  //package data
  const [packageData, setPackageData] = useState([]);
  //package dataErr
  const [packageDataErr, setPackageDataErr] = useState([]);
  //affect data change depended on delete and add responses
  const [difResponses, setDifResponses] = useState(null);
  //package check all
  const [checkAll, setCheckAll] = useState(false);
  //search value and transportation
  const [search, setSearch] = useState({
    key: "",
  });
  //selected Packages
  const [selectedPackages, setSelectedPackages] = useState({
    pack_id: [],
  });
  //current page
  const [currentPage, setCurrentPage] = useState(0);
  //total count
  const [total, setTotal] = useState(0);
  //shipment code for express
  const [shipmentCode, setShipmentCode] = useState("");
  //is loading state
  const [isLoading, setIsLoading] = useState(false);
  //transportation
  const [transportation, setTransportation] = useState({
    transport: "",
    secured: "",
  });
  //shipments
  const [shipments, setShipments] = useState([]);
  //border declaration status - track most critical among all packages
  const [borderDeclarStatus, setBorderDeclarStatus] = useState(0);

  //handle selected Packages
  const handleSelectedPackages = (value) => {
    if (!selectedPackages.pack_id.includes(value)) {
      setSelectedPackages((prev) => ({ pack_id: [...prev.pack_id, value] }));
    }
  };
  //handle delete selected Packages
  const handleDeleteSelectedPackages = (value) => {
    setSelectedPackages((prev) => ({
      pack_id: prev.pack_id.filter((obj) => {
        return obj !== value;
      }),
    }));
  };

  //handle change search value
  const handleSearchvalue = (e, inputName) => {
    setSearch((prev) => ({ ...prev, [inputName]: e.target.value }));
  };

  //handle transportation value
  const handleSearchTransport = (value, inputName) => {
    setTransportation((prev) => ({ ...prev, [inputName]: value }));
  };

  //handle checking
  const handleCheckAll = (e) => {
    setCheckAll(e.target.checked);
  };

  //current filial handler
  const handleCurrentPlaceId = (value) => {
    setCurrentPlaceId(value);
    setCheckAll(false);
    setSelectedPackages({
      pack_id: [],
    });
  };

  //handle delete package
  const handleDeletePackage = async (id) => {
    try {
      const res = await axiosAdmin.delete(`/packs/${id}`);
      if (res) {
        setDifResponses(res);
        successToast(t("admin_package_deleted"));
      }
    } catch (err) {
      setPackageDataErr(err);
      errorToast(apiError(err, t("admin_error")));
    }
  };

  const handleUndeclared = (pack) => {
    axiosAdmin
      .get(`/packs/unDeclare/${pack}`)
      .then((res) => {
        successToast(t("admin_action_success"));
        setDifResponses(res);
      })
      .catch(() => {
        errorToast(t("admin_error"));
      });
  };

  //const handle send package
  const handleSendAllPackages = async () => {
    try {
      const res = await axiosAdmin.post(`/packs/set`, {
        status: "2.5",
        ids: [...selectedPackages.pack_id],
      });
      if (res) {
        successToast(t("admin_packages_received"));
        setCheckAll(false);
        setSelectedPackages({
          pack_id: [],
        });
        setDifResponses(res);
      }
    } catch (err) {
      errorToast(apiError(err, t("admin_error")));
    }
  };

  //data fetching
  const handleParcelInfo = () => {
    setIsLoading(true);
    axiosAdmin
      .get(
        `/packs?status=${2}&abroad_office_id=${currentPlaceId}&page=${
          currentPage + 1
        }` +
          `&per_page=${20}&addon_transp=${
            transportation.transport
          }&searchValue=${search.key}` +
          `&secured=${transportation.secured}` +
          `&repack=${
            transportation.repack === "false" ? "" : transportation.repack
          }`
      )
      .then(({ data }) => {
        setPackageData(data.data);
        setTotal(data.meta.total);
        setIsLoading(false);
      })
      .catch((err) => {
        setPackageDataErr(err);
      });
  };

  //handle sent pack back
  const handleSendBack = (id) => {
    axiosAdmin
      .get(`/packs/back/${id}`)
      .then((res) => {
        successToast(t("admin_returned_to_prev"));
        setDifResponses(res);
      })
      .catch((err) => {
        errorToast(err?.response?.data?.error || t("admin_error"));
      });
  };

  //update user
  // A different recipient opens the address popup first: the delivery
  // address belongs to the customer, and the previous owner's must not ride
  // along. Same recipient saves straight away.
  const [recipientPrompt, setRecipientPrompt] = useState(null);
  const handleGiveUserToParcel = (newData, packId) => {
    const current = packageData.find((p) => p.id === packId);
    const nextUser = newData?.userId;
    if (nextUser && String(nextUser) !== String(current?.user_id ?? "")) {
      setRecipientPrompt({ newData, packId, userId: nextUser, tracking: current?.tracking_number });
      return;
    }
    saveParcelEdit(newData, packId);
  };

  const saveParcelEdit = (newData, packId, addressId) => {
    axiosAdmin
      .put(`/packs/${packId}`, {
        user_id: newData.userId,
        ...(addressId !== undefined ? { user_address_id: addressId } : {}),
        total_weight: newData.weight,
        shipment_code: newData.flightNumber,
        transport_price: newData.transport_price,
        dementionY: newData.dementionY,
        dementionZ: newData.dementionZ,
        dementionX: newData.dementionX,
        addon_secur: newData.addon_secur,
      })
      .then((res) => {
        successToast(t("admin_data_updated"));
        setDifResponses(res);
        // The group changed, so siblings may have flipped colour on a page
        // the operator is not looking at.
        if (res?.data?.border_declaration?.length) {
          setBorderNotice(res.data.border_declaration);
        }
      })
      .catch(() => {
        errorToast(t("admin_error"));
      });
  };

  //on barcode scanner using
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleParcelInfo();
    }
  };

  //package data fetching
  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  //compute most critical border_declar status
  useEffect(() => {
    if (packageData && packageData.length > 0) {
      // Priority: 3 (customs) > 1 (needs declaration) > 2 (declared) > 0 (not needed)
      let maxStatus = 0;
      for (let pkg of packageData) {
        const status = pkg.border_declar ?? 0;
        // 3 is highest priority (in customs), then 1 (needs declaration), then 2 (declared), then 0
        if (status === 3) {
          maxStatus = 3;
          break; // Stop at highest priority
        } else if (status === 1 && maxStatus !== 3) {
          maxStatus = 1;
        } else if (status === 2 && maxStatus === 0) {
          maxStatus = 2;
        }
      }
      setBorderDeclarStatus(maxStatus);
    } else {
      setBorderDeclarStatus(0);
    }
  }, [packageData]);

  //package data fetching
  useEffect(() => {
    handleParcelInfo();
  }, [currentPlaceId, difResponses, transportation, currentPage]);

  //package data fetching
  useEffect(() => {
    if (selectedPackages.pack_id.length > 0) {
      setCheckAll(false);
      setSelectedPackages({
        pack_id: [],
      });
    }
  }, [currentPage]);

  useEffect(() => {
    axiosAdmin
      .get(`/shipments?per_page=500`)
      .then(({ data }) => {
        setShipments((prev) => [
          ...data.data.map((item) => {
            return {
              value: item.code,
              name: item.code,
            };
          }),
        ]);
      })
      .catch((err) => {});
  }, []);

  return (
    <div
      className={`xl:ml-[280px] mt-[55px] dark:bg-slate-900 min-h-screen p-10 sm:p-5 pb-24 sm:pb-24 ${
        borderDeclarStatus === 0 ? 'bg-slate-100' :
        borderDeclarStatus === 1 ? 'bg-red-50' :
        borderDeclarStatus === 2 ? 'bg-green-50' :
        borderDeclarStatus === 3 ? 'bg-yellow-50' :
        'bg-slate-100'
      }`}
    >
      <div className="w-full overflow-auto scrollbar mb-5">
        <div className="flex gap-1 w-full md:w-[1000px] p-2">
          {foreignAdresses.map((item) => {
            return (
              <div
                key={item.id}
                className={`w-full flex items-center p-3 rounded-lg gap-5 cursor-pointer
                    shadow-[rgba(50,50,93,0.25)_0px_2px_5px_-1px,rgba(0,0,0,0.3)_0px_1px_3px_-1px] 
                    ${
                      currentPlaceId === item.id
                        ? "border border-green-500 bg-green-100"
                        : "bg-white dark:bg-slate-700 "
                    }`}
              >
                <div className="w-full flex flex-col gap-2">
                  <p className="text-[14px] text-[#8e95a8]">
                    {`${item.country} address`}
                  </p>
                  <div className="flex gap-1">
                    <div
                      onClick={() => {
                        handleCurrentPlaceId(item.id);
                      }}
                      className="w-full rounded-lg bg-green-400
                 flex items-center justify-center gap-1 p-1 py-3 text-white"
                    >
                      <FaRegHandPointRight />
                      <p>აირჩიე</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <h2 className="text-slate-400 text-2xl">{t("admin_sent_packages")}</h2>
      <div className="w-full">
        <div className="bg-yellow mt-2 flex gap-1 sm:flex-col">
          <div
            className="bg-white  pv-2 flex items-center hover:outline-slate-400 hover:outline-2
            hover:outline gap-x-1  px-3.5 py-1 rounded-lg dark:bg-slate-800 border-gray-300
            border dark:border-slate-600 w-9/12 sm:w-full"
          >
            <input
              type="search"
              placeholder={t("admin_search")}
              onChange={(e) => {
                handleSearchvalue(e, "key");
              }}
              className="w-full h-8 pl-1.5 focus:outline-none dark:text-white
                                dark:bg-slate-800 border-0"
              onKeyDown={handleKeyPress}
            />
          </div>

          <SelectInputValueId
            options={shipments}
            inputName={"shipmentId"}
            inputSetter={(flight_num, _) => {
              setShipmentCode(flight_num);
            }}
            value={shipmentCode}
            defaultText={t("admin_flight_number_for_excel")}
            needClear={true}
          />

          <AdminDownloadLink
            filename="packs.xlsx"
            href={
              `${
                process.env.REACT_APP_API_URL
              }/api/exportPacks?status=${2}&abroad_office_id=${currentPlaceId}` +
              `&shipment_code=${shipmentCode}` +
              `&repack=${
                transportation.repack === "false" ? "" : transportation.repack
              }`
            }
            className="w-3/12 rounded-lg bg-green-400 sm:w-full
                 flex items-center justify-center p-1 py-3 text-white"
          >
            EXPORT CSV
          </AdminDownloadLink>
        </div>
        <div className="flex items-center justify-between my-2 flex-wrap gap-3 mt-2">
          <div className="flex gap-2 items-center sm:order-2 sm:flex-col sm:items-start">
            <CheckBox
              name={t("admin_transportation")}
              inputName={"transport"}
              handleCarData={handleSearchTransport}
            />
            <CheckBox
              name={t("admin_insured_packages")}
              inputName={"secured"}
              handleCarData={handleSearchTransport}
            />
            <div className="flex items-center gap-3">
              <input
                checked={checkAll}
                onChange={(e) => {
                  handleCheckAll(e);
                }}
                type="checkbox"
              />
              <p className="text-sm text-gray-500">{t("admin_select_all")}</p>
            </div>
            <CheckBox
              name={t("admin_repacked_packages")}
              inputName={"repack"}
              handleCarData={handleSearchTransport}
            />
          </div>
          <div className="flex gap-2 sm:w-full">
            <div
              onClick={handleSendAllPackages}
              className="rounded-lg bg-green-400 cursor-pointer
flex items-center justify-center gap-1 p-1 py-3 text-white sm:w-full"
            >
              <p>{t("admin_receive_selected")}</p>
            </div>
          </div>
        </div>
        <div className="w-full flex gap-3 flex-wrap mt-5">
          <PaginatedItems pageNumberSetter={setCurrentPage} dataLength={total}>
            {isLoading ? (
              <div className="mt-5 w-full flex items-center justify-center min-h-[200px]">
                <BiLoaderAlt className="w-20 h-20 animate-spin text-green-500" />
              </div>
            ) : isLoading === false && packageData.length === 0 ? (
              <div className="mt-5 w-full flex items-center justify-center min-h-[200px]">
                <p className="text-slate-600 text-sm">{t("admin_no_data_found")}</p>
              </div>
            ) : (
              <div className="w-full flex flex-col gap-2">
                {packageData.map((item) => {
                  return (
                    <SentItem
                      key={item.id}
                      data={item}
                      handleDeletePackage={handleDeletePackage}
                      setCheckedAll={checkAll}
                      handleSelectedPackages={handleSelectedPackages}
                      handleDeleteSelectedPackages={
                        handleDeleteSelectedPackages
                      }
                      setDifResponses={setDifResponses}
                      handleSendBack={handleSendBack}
                      handleGiveUserToParcel={handleGiveUserToParcel}
                      handleUndeclared={handleUndeclared}
                    />
                  );
                })}
              </div>
            )}
          </PaginatedItems>
        </div>
      </div>
      <BorderChangeNotice
        reports={borderNotice}
        onClose={() => setBorderNotice(null)}
      />
      {recipientPrompt && (
        <RecipientAddressModal
          packId={recipientPrompt.packId}
          tracking={recipientPrompt.tracking}
          userId={recipientPrompt.userId}
          onCancel={() => setRecipientPrompt(null)}
          onConfirm={(addressId) => {
            saveParcelEdit(recipientPrompt.newData, recipientPrompt.packId, addressId);
            setRecipientPrompt(null);
          }}
        />
      )}
    </div>
  );
};

export default SentPackage;
